import { useCallback, useEffect, useMemo, useState } from "react";
import { Activity, AlertTriangle, BarChart3, Database, RefreshCw, Shield, Target, TrendingUp, Wallet } from "lucide-react";
import { Area, AreaChart, Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

import { API_BASE as API } from "../config";

function fmtUsd(v, digits = 2) {
  const n = Number(v || 0);
  return `${n < 0 ? "-" : ""}$${Math.abs(n).toLocaleString(undefined, { maximumFractionDigits: digits, minimumFractionDigits: digits })}`;
}

function fmtPct(v, digits = 1) {
  const n = Number(v || 0);
  return `${n > 0 ? "+" : ""}${n.toFixed(digits)}%`;
}

function Card({ title, icon: Icon, children, className = "" }) {
  return (
    <section className={`rounded-lg border border-zinc-800 bg-zinc-950/50 ${className}`}>
      <div className="flex items-center gap-2 border-b border-zinc-800 px-4 py-3">
        {Icon && <Icon className="h-4 w-4 text-teal-400" />}
        <h3 className="text-sm font-bold text-zinc-100">{title}</h3>
      </div>
      <div className="p-4">{children}</div>
    </section>
  );
}

function Metric({ label, value, sub, tone = "neutral" }) {
  const color = {
    good: "text-emerald-400",
    bad: "text-red-400",
    warn: "text-amber-400",
    neutral: "text-zinc-100",
  }[tone];
  return (
    <div className="rounded-lg border border-zinc-800 bg-zinc-900/45 p-3">
      <div className="text-[10px] uppercase tracking-wide text-zinc-500">{label}</div>
      <div className={`mt-1 font-mono text-xl font-black ${color}`}>{value}</div>
      {sub && <div className="mt-1 text-[11px] text-zinc-500">{sub}</div>}
    </div>
  );
}

export default function CodexRiskDashboard({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchData = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError("");
    try {
      const r = await fetch(`${API}/api/eu-bot/dca/codex-dashboard`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setData(await r.json());
    } catch (e) {
      setError(e?.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
    const id = setInterval(fetchData, 30000);
    return () => clearInterval(id);
  }, [fetchData]);

  const soData = useMemo(() => {
    const dist = data?.deal_stats?.so_distribution || {};
    return Object.keys(dist).sort((a, b) => Number(a) - Number(b)).map(k => ({ so: k, deals: dist[k] }));
  }, [data]);

  const balanceData = useMemo(() => {
    return [...(data?.balances || [])].reverse().map(b => ({ date: b.date, usd: Number(b.total_usd || 0) }));
  }, [data]);

  if (loading && !data) {
    return <div className="py-20 text-center font-mono text-sm text-zinc-600">Loading Codex dashboard...</div>;
  }

  if (error && !data) {
    return <div className="rounded-lg border border-red-800 bg-red-950/20 p-4 text-sm text-red-300">Codex dashboard error: {error}</div>;
  }

  const open = data?.open_deal || {};
  const deals = data?.deal_stats || {};
  const fills = data?.fill_stats || {};
  const minerTop = data?.miner?.top || {};
  const stance = data?.operator_view?.stance || "unknown";
  const isDeep = (open.safety_orders_filled || 0) >= 4;

  return (
    <div className="space-y-4" data-testid="codex-risk-dashboard">
      <div className="flex flex-col gap-3 rounded-lg border border-zinc-800 bg-zinc-950/60 p-4 md:flex-row md:items-center md:justify-between">
        <div>
          <div className="flex items-center gap-2 text-sm font-bold text-zinc-100">
            <Shield className="h-4 w-4 text-teal-400" />
            Codex Operator Dashboard
          </div>
          <div className="mt-1 text-xs text-zinc-500">
            Read-only view. Uses live state, Mongo deals, Binance balance snapshots, fill logs, and miner configs.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className={`rounded border px-2 py-1 text-xs font-mono ${stance === "hold_and_fix_safety" ? "border-amber-500/30 bg-amber-500/10 text-amber-300" : "border-emerald-500/30 bg-emerald-500/10 text-emerald-300"}`}>
            {stance.replaceAll("_", " ").toUpperCase()}
          </span>
          <button onClick={fetchData} className="inline-flex items-center gap-1 rounded border border-zinc-700 px-3 py-1.5 text-xs text-zinc-300 hover:border-zinc-500">
            <RefreshCw className="h-3.5 w-3.5" />
            Refresh
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        <Metric label="Open Risk" value={`${open.safety_orders_filled || 0}/${open.max_safety_orders || 0} SO`} sub={`${fmtUsd(open.total_invested)} invested`} tone={isDeep ? "warn" : "neutral"} />
        <Metric label="Unrealized" value={fmtPct(open.unrealized_pct)} sub={fmtUsd(open.unrealized_pnl)} tone={(open.unrealized_pct || 0) < -3 ? "bad" : "neutral"} />
        <Metric label="Move To TP" value={fmtPct(open.move_to_tp_pct)} sub={`TP ${open.tp_pct || 0}%`} tone={(open.move_to_tp_pct || 0) > 5 ? "warn" : "good"} />
        <Metric label="Closed Deals" value={`${deals.wins || 0}W/${deals.losses || 0}L`} sub={`${deals.count || 0} total`} tone="good" />
        <Metric label="Realized PnL" value={fmtUsd(deals.total_pnl)} sub={`median ${fmtUsd(deals.median_profit)}`} tone={(deals.total_pnl || 0) >= 0 ? "good" : "bad"} />
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
        <Card title="Current Deal" icon={Target} className="xl:col-span-1">
          <div className="space-y-3 text-sm">
            {[
              ["Bot", open.bot_id],
              ["Symbol", open.symbol],
              ["Avg Entry", open.avg_entry_price],
              ["Current", open.current_price],
              ["TP Price", open.tp_price],
              ["Capital At Risk", `${open.capital_at_risk_pct || 0}%`],
              ["Free Capital", `${open.free_capital_pct || 0}%`],
            ].map(([k, v]) => (
              <div key={k} className="flex justify-between border-b border-zinc-800/60 pb-2">
                <span className="text-zinc-500">{k}</span>
                <span className="font-mono text-zinc-100">{v ?? "-"}</span>
              </div>
            ))}
          </div>
        </Card>

        <Card title="Deal Shape" icon={BarChart3} className="xl:col-span-1">
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={soData}>
                <CartesianGrid stroke="#27272a" vertical={false} />
                <XAxis dataKey="so" stroke="#71717a" fontSize={11} />
                <YAxis stroke="#71717a" fontSize={11} />
                <Tooltip contentStyle={{ background: "#09090b", border: "1px solid #3f3f46" }} />
                <Bar dataKey="deals" radius={[4, 4, 0, 0]}>
                  {soData.map((d) => <Cell key={d.so} fill={Number(d.so) >= 4 ? "#f59e0b" : "#14b8a6"} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-2 text-xs text-zinc-500">Average SOs: {Number(deals.avg_safety_orders || 0).toFixed(2)} · Average hold: {Number(deals.avg_duration_hours || 0).toFixed(2)}h</div>
        </Card>

        <Card title="Fill Drag" icon={Activity} className="xl:col-span-1">
          <div className="grid grid-cols-2 gap-2">
            <Metric label="Buy Median" value={`${Number(fills.buy?.median_slippage_bps || 0).toFixed(1)} bps`} sub={`${fills.buy?.count || 0} fills`} tone="warn" />
            <Metric label="Sell Median" value={`${Number(fills.sell?.median_slippage_bps || 0).toFixed(1)} bps`} sub={`${fills.sell?.count || 0} fills`} tone="warn" />
            <Metric label="Buy P90" value={`${Number(fills.buy?.p90_slippage_bps || 0).toFixed(1)} bps`} sub="tail cost" tone="bad" />
            <Metric label="Sell P90" value={`${Number(fills.sell?.p90_slippage_bps || 0).toFixed(1)} bps`} sub="tail cost" tone="bad" />
          </div>
          <div className="mt-3 text-xs text-zinc-500">Commission assets: {Object.entries(fills.commission_assets || {}).map(([k, v]) => `${k}:${v}`).join(" · ") || "-"}</div>
        </Card>
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <Card title="Miner Alignment" icon={Database}>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <Metric label="Top TP" value={`${minerTop.tp ?? "-"}%`} sub={minerTop.timeframe || "-"} />
            <Metric label="Miner APY" value={`${Number(minerTop.apy || 0).toFixed(1)}%`} sub={`${minerTop.deals || 0} deals`} tone="good" />
            <Metric label="Miner DD" value={`${Number(minerTop.max_dd || 0).toFixed(1)}%`} sub="historical max" tone={Number(minerTop.max_dd || 0) > 40 ? "warn" : "neutral"} />
            <Metric label="Max SO" value={minerTop.max_so ?? "-"} sub={`vs ${open.max_safety_orders || 0} live`} />
          </div>
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="text-zinc-500">
                <tr><th className="py-2">Coin</th><th>TF</th><th>TP</th><th>APY</th><th>DD</th><th>Deals</th></tr>
              </thead>
              <tbody>
                {(data?.candidate_rank || []).map((c) => (
                  <tr key={c.coin} className="border-t border-zinc-800 text-zinc-300">
                    <td className="py-2 font-mono">{c.coin}</td>
                    <td>{c.timeframe}</td>
                    <td>{c.tp}%</td>
                    <td className="text-emerald-400">{Number(c.apy || 0).toFixed(1)}%</td>
                    <td className={Number(c.max_dd || 0) > 45 ? "text-amber-400" : "text-zinc-300"}>{Number(c.max_dd || 0).toFixed(1)}%</td>
                    <td>{c.deals}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        <Card title="Binance Balance" icon={Wallet}>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={balanceData}>
                <defs>
                  <linearGradient id="codexBalance" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#14b8a6" stopOpacity={0.35}/>
                    <stop offset="95%" stopColor="#14b8a6" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#27272a" vertical={false} />
                <XAxis dataKey="date" stroke="#71717a" fontSize={11} />
                <YAxis stroke="#71717a" fontSize={11} domain={["dataMin - 100", "dataMax + 100"]} />
                <Tooltip formatter={(v) => fmtUsd(v)} contentStyle={{ background: "#09090b", border: "1px solid #3f3f46" }} />
                <Area type="monotone" dataKey="usd" stroke="#14b8a6" fill="url(#codexBalance)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <Card title="Safety Findings" icon={AlertTriangle}>
          <div className="space-y-2">
            {(data?.risk_findings || []).map((f) => (
              <div key={f.title} className="rounded border border-zinc-800 bg-zinc-900/40 p-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-sm font-bold text-zinc-100">{f.title}</div>
                    <div className="mt-1 text-xs text-zinc-500">{f.impact}</div>
                    <div className="mt-2 text-xs text-teal-300">{f.fix}</div>
                  </div>
                  <span className={`rounded px-2 py-1 text-xs font-mono ${f.priority === "P1" ? "bg-red-500/10 text-red-300" : "bg-amber-500/10 text-amber-300"}`}>{f.priority}</span>
                </div>
              </div>
            ))}
          </div>
        </Card>

        <Card title="Operator Plan" icon={TrendingUp}>
          <div className="space-y-3 text-sm">
            <div className="rounded border border-teal-500/20 bg-teal-500/5 p-3">
              <div className="text-xs uppercase tracking-wide text-teal-300">Next cycle tuning</div>
              <div className="mt-1 font-mono text-zinc-100">
                ENJ: TP {data?.operator_view?.next_strategy_change?.from?.tp_pct}% → {data?.operator_view?.next_strategy_change?.to?.tp_pct}% ·
                VS {data?.operator_view?.next_strategy_change?.from?.vol_scale} → {data?.operator_view?.next_strategy_change?.to?.vol_scale}
              </div>
            </div>
            {(data?.operator_view?.do_not_do || []).map((x) => (
              <div key={x} className="rounded border border-zinc-800 bg-zinc-900/40 px-3 py-2 text-xs text-zinc-400">{x}</div>
            ))}
            <div className="text-xs text-zinc-600">
              Fleet snapshot age: {data?.fleet_health?.snapshot_age_sec ?? "-"}s · Aggregate move to TP: {data?.fleet_health?.aggregate_move_to_tp_pct ?? "-"}%
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
